import React from 'react';
import { Link } from 'react-router-dom';

function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/k46v5h98_Systemklar%20Logo%20med%20Sjekkmerke.png" 
                alt="Systemklar" 
                className="h-8"
              />
            </Link>
            <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700">
              Logg inn
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm border p-8 md:p-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Vilkår for bruk</h1>
          <p className="text-gray-500 mb-8">Sist oppdatert: April 2026</p>

          <div className="prose prose-gray max-w-none">
            {/* 1. Innledning */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Innledning og aksept av vilkår</h2>
              <p className="text-gray-700 mb-4">
                Disse vilkårene («Vilkårene») regulerer din bruk av Systemklar-plattformen («Tjenesten»), 
                som leveres av Valoria Solutions AS («vi», «oss» eller «Selskapet»).
              </p>
              <p className="text-gray-700 mb-4">
                Ved å opprette en konto eller bruke Tjenesten, bekrefter du at du har lest, forstått og 
                akseptert disse Vilkårene. Dersom du ikke aksepterer Vilkårene, skal du ikke bruke Tjenesten.
              </p>
            </section>

            {/* 2. Tjenestebeskrivelse */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Tjenestebeskrivelse</h2>
              <p className="text-gray-700 mb-4">
                Systemklar er et digitalt <strong>støtteverktøy</strong> som hjelper virksomheter med å:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>Generere forslag til internkontrollsystemer (HMS / IK)</li>
                <li>Holde oversikt over oppgaver og frister</li>
                <li>Registrere og følge opp avvik</li>
                <li>Forberede seg til sertifiseringer som ISO og Miljøfyrtårn</li>
              </ul>
              
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 my-6">
                <p className="text-amber-800 font-medium mb-2">
                  <strong>Viktig:</strong> Systemklar er et støtteverktøy og <u>garanterer ikke</u> etterlevelse 
                  av lover eller forskrifter.
                </p>
                <p className="text-amber-700">
                  Tjenesten genererer forslag og maler basert på brukerens input. Alt innhold som 
                  genereres er <strong>veiledende</strong> og må gjennomgås, tilpasses og valideres 
                  av brukeren før det tas i bruk.
                </p>
              </div>

              <div className="bg-gray-100 rounded-lg p-4">
                <p className="text-gray-700 text-sm">
                  <strong>Merk:</strong> Tjenesten utgjør ikke juridisk, regulatorisk eller profesjonell 
                  rådgivning. Ved behov for slik rådgivning bør du kontakte kvalifiserte fagpersoner.
                </p>
              </div>
            </section>

            {/* 3. Brukerens ansvar */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Brukerens ansvar</h2>
              <p className="text-gray-700 mb-4">
                Ved å bruke Tjenesten, aksepterer du følgende:
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-4">
                <h3 className="font-semibold text-red-800 mb-3">Du er fullt ansvarlig for:</h3>
                <ul className="list-disc pl-6 text-red-700 space-y-3">
                  <li>
                    <strong>Gjennomgang, tilpasning og validering:</strong> Brukeren er fullt ansvarlig for 
                    å gjennomgå, tilpasse og validere alt innhold som genereres av Tjenesten før det tas i bruk.
                  </li>
                  <li>
                    <strong>Lovmessig etterlevelse:</strong> Å sikre at ditt internkontrollsystem oppfyller 
                    kravene i Internkontrollforskriften, Arbeidsmiljøloven og andre relevante lover og forskrifter.
                  </li>
                  <li>
                    <strong>Faglig vurdering:</strong> Å innhente nødvendig faglig bistand (f.eks. HMS-rådgiver, 
                    advokat eller bedriftshelsetjeneste) der det er behov for det.
                  </li>
                  <li>
                    <strong>Oppdatering:</strong> Å holde internkontrollsystemet oppdatert i henhold til 
                    endringer i lovverk, virksomhetens drift eller andre relevante forhold.
                  </li>
                  <li>
                    <strong>Nøyaktighet av input:</strong> At informasjonen du oppgir til Tjenesten er 
                    korrekt og fullstendig.
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
                <p className="text-amber-800 font-medium">
                  Bruk av Tjenesten skjer på brukerens egen risiko. Systemklar er et hjelpemiddel – 
                  ikke en erstatning for faglig kompetanse eller grundig arbeid med HMS i din virksomhet.
                </p>
              </div>
            </section>

            {/* 4. Abonnement og betaling */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Abonnement og betaling</h2>
              
              <h3 className="font-semibold text-gray-800 mb-2">4.1 Gratis prøveperiode</h3>
              <p className="text-gray-700 mb-4">
                Nye brukere får tilgang til en gratis prøveperiode med begrenset funksjonalitet. 
                Etter prøveperioden kreves et betalt abonnement for fortsatt bruk av alle funksjoner.
              </p>

              <h3 className="font-semibold text-gray-800 mb-2">4.2 Automatisk fornyelse</h3>
              <p className="text-gray-700 mb-4">
                Abonnementet fornyes automatisk ved utløp av hver betalingsperiode (månedlig eller årlig), 
                med mindre du aktivt sier opp før fornyelsesdatoen. Du vil bli varslet før fornyelse.
              </p>

              <h3 className="font-semibold text-gray-800 mb-2">4.3 Ingen refusjon for påbegynt periode</h3>
              <p className="text-gray-700 mb-4">
                Ved oppsigelse av abonnementet gis det <strong>ingen refusjon</strong> for inneværende 
                betalingsperiode. Du beholder tilgang til Tjenesten ut perioden du har betalt for.
              </p>

              <h3 className="font-semibold text-gray-800 mb-2">4.4 Prisendringer</h3>
              <p className="text-gray-700 mb-4">
                Vi forbeholder oss retten til å endre prisene. Prisendringer varsles minst 30 dager 
                før de trer i kraft. Fortsatt bruk etter prisendring anses som aksept av nye priser.
              </p>
            </section>

            {/* 5. Tilgjengelighet */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Tilgjengelighet og vedlikehold</h2>
              <p className="text-gray-700 mb-4">
                Tjenesten leveres «som den er» («as-is»). Vi tilstreber høy oppetid, men garanterer ikke 
                at Tjenesten vil være tilgjengelig til enhver tid eller være fri for feil.
              </p>
              <p className="text-gray-700">
                Vi kan utføre planlagt vedlikehold som kan påvirke tilgjengeligheten. Ved større 
                planlagte avbrudd vil vi forsøke å varsle brukere på forhånd.
              </p>
            </section>

            {/* 6. Ansvarsbegrensning */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Ansvarsbegrensning</h2>
              
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                <p className="text-red-800 font-medium">
                  Bruk av Tjenesten skjer på brukerens egen risiko. Tjenesten utgjør <strong>ikke</strong> juridisk, 
                  regulatorisk eller profesjonell rådgivning.
                </p>
              </div>

              <div className="bg-gray-100 border border-gray-300 rounded-lg p-5 mb-4">
                <p className="text-gray-800 font-medium mb-3">
                  Selskapet er <strong>IKKE</strong> ansvarlig for:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>
                    <strong>Manglende etterlevelse:</strong> Eventuelle mangler ved brukerens 
                    internkontrollsystem eller manglende etterlevelse av lover og forskrifter
                  </li>
                  <li>
                    <strong>Feil i generert innhold:</strong> Feil, mangler eller unøyaktigheter 
                    i innhold generert av Tjenesten, inkludert AI-generert innhold
                  </li>
                  <li>
                    <strong>Indirekte tap:</strong> Indirekte tap, herunder, men ikke begrenset til, 
                    tap av fortjeneste, tap av data eller driftsavbrudd
                  </li>
                  <li>
                    <strong>Bøter og sanksjoner:</strong> Bøter, gebyrer eller andre sanksjoner 
                    fra myndigheter som følge av brukerens manglende etterlevelse
                  </li>
                  <li>
                    <strong>Tap av data:</strong> Tap av data som skyldes forhold utenfor vår kontroll
                  </li>
                  <li>
                    <strong>Tredjepartstjenester:</strong> Feil eller problemer med tredjepartstjenester 
                    som Tjenesten benytter (inkludert AI-leverandører og betalingsløsninger)
                  </li>
                </ul>
              </div>

              <p className="text-gray-700">
                Ethvert direkte ansvar er begrenset til beløpet brukeren har betalt for Tjenesten 
                i løpet av de siste 12 månedene.
              </p>
            </section>

            {/* 7. Force Majeure */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Force majeure</h2>
              <p className="text-gray-700">
                Selskapet er ikke ansvarlig for manglende oppfyllelse av sine forpliktelser som skyldes 
                forhold utenfor Selskapets rimelige kontroll, herunder, men ikke begrenset til: 
                strømbrudd, nettverksfeil, myndighetstiltak, streik, naturkatastrofer, pandemi 
                eller svikt i tredjepartstjenester.
              </p>
            </section>

            {/* 8. Immaterielle rettigheter */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Immaterielle rettigheter og dataeierskap</h2>
              <p className="text-gray-700 mb-4">
                Systemklar, inkludert programvare, design, logoer og dokumentasjon, er beskyttet av 
                opphavsrett og andre immaterielle rettigheter som tilhører Selskapet.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Dataeierskap:</strong> Brukeren beholder eiendomsretten til sine egne data 
                og alt innhold som genereres gjennom Tjenesten. Du gir oss en begrenset lisens til 
                å lagre og behandle dette innholdet utelukkende for å levere Tjenesten.
              </p>
            </section>

            {/* 9. Oppsigelse */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Oppsigelse</h2>
              <p className="text-gray-700 mb-4">
                Du kan når som helst si opp ditt abonnement via kontoinnstillingene. Ved oppsigelse 
                beholder du tilgang ut inneværende betalingsperiode.
              </p>
              <p className="text-gray-700">
                Vi kan suspendere eller avslutte din tilgang ved brudd på disse Vilkårene, 
                manglende betaling, eller av andre saklige grunner.
              </p>
            </section>

            {/* 10. Endringer */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Endringer i vilkårene</h2>
              <p className="text-gray-700">
                Vi kan oppdatere disse Vilkårene fra tid til annen. Vesentlige endringer varsles via 
                e-post eller i Tjenesten. Fortsatt bruk av Tjenesten etter endringer anses som aksept 
                av de oppdaterte Vilkårene.
              </p>
            </section>

            {/* 11. Lovvalg og tvister */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Lovvalg og tvister</h2>
              <p className="text-gray-700 mb-4">
                Disse Vilkårene er underlagt <strong>norsk rett</strong>.
              </p>
              <p className="text-gray-700">
                Eventuelle tvister skal søkes løst i minnelighet. Dersom dette ikke fører frem, 
                skal tvisten avgjøres av norske domstoler med Oslo tingrett som verneting.
              </p>
            </section>

            {/* 12. Kontakt */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Kontaktinformasjon</h2>
              <p className="text-gray-700 mb-4">
                Har du spørsmål om disse Vilkårene, kan du kontakte oss:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700"><strong>Valoria Solutions AS</strong></p>
                <p className="text-gray-700">E-post: post@valoria.no</p>
              </div>
            </section>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-8 flex justify-center gap-6 text-sm">
          <Link to="/privacy" className="text-blue-600 hover:text-blue-700">
            Personvernerklæring
          </Link>
          <Link to="/disclaimer" className="text-blue-600 hover:text-blue-700">
            Ansvarsfraskrivelse
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-gray-500">
          © 2025 Valoria Solutions AS. Alle rettigheter reservert.
        </div>
      </footer>
    </div>
  );
}

export default TermsOfService;
